import { useState, type CSSProperties } from "react";
import { Alert, Collapse, Drawer, Empty, Image, Tabs, Tag, Timeline, Typography, theme } from "antd";
import { ArrowRightOutlined, EyeOutlined, PictureOutlined } from "@ant-design/icons";
import { dateTime } from "./domain";
import { Resource, useResource } from "./shared";
import { creativeURL, welfareCreative, placementLabels, reviewLabels, type AdDetail, type AdPayload, type Creative } from "./advertising-domain";
import "./advertising-detail.css";

const reviewColors = { draft: "default", pending_review: "processing", approved: "success", rejected: "error" };

function CreativePreview({ creative }: { creative: Creative }) {
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const hasImage = creative.image_url && creativeURL(creative.image_url) && !failed;
  return <article className="ad-detail-creative">
    <div className="ad-detail-creative-heading"><strong>{placementLabels[creative.placement]}</strong><span>排序 {creative.sort_order}</span></div>
    {hasImage ? <div className="ad-detail-image">
      <Image src={creative.image_url} alt={placementLabels[creative.placement]} onError={() => setFailed(true)} preview={{ visible, onVisibleChange: setVisible, mask: false }} />
      <button type="button" className="ad-detail-image-open" aria-label={`查看${placementLabels[creative.placement]}大图`} onClick={() => setVisible(true)}><span className="ad-detail-image-action"><EyeOutlined /> 查看大图</span></button>
    </div> : <div className="ad-detail-image-empty"><PictureOutlined /><span>{failed ? "图片暂时无法加载" : "未设置广告图片"}</span></div>}
  </article>;
}

function PayloadDetail({ payload, showTitle = false }: { payload: AdPayload; showTitle?: boolean }) {
  return <div className="ad-detail-content">
    {showTitle && <div className="ad-detail-version-name"><span>广告名称</span><strong>{payload.title}</strong></div>}
    <section className="ad-detail-section" aria-label="投放信息">
      <div className="ad-detail-section-heading"><h3>投放信息</h3><span>北京时间 · UTC+8</span></div>
      <div className="ad-detail-schedule">
        <div><span>开始时间</span><strong>{dateTime(payload.start_at)}</strong></div>
        <ArrowRightOutlined className="ad-detail-schedule-arrow" />
        <div><span>结束时间</span><strong>{dateTime(payload.end_at)}</strong></div>
      </div>
      <dl className="ad-detail-fields">
        <div><dt>跳转地址</dt><dd><Typography.Text copyable={{ text: payload.link_url, tooltips: ["复制跳转地址", "已复制"] }}>{payload.link_url || "未设置"}</Typography.Text></dd></div>
        <div><dt>渠道标记</dt><dd className="ad-detail-channel-setting"><Tag bordered={false} color={payload.attribution_enabled ? "blue" : "default"}>{payload.attribution_enabled ? "已开启" : "未开启"}</Tag><span className="ad-detail-parameter">参数名 <code>{payload.click_id_param || "channel_id"}</code></span></dd></div>
      </dl>
    </section>
    <section className="ad-detail-section" aria-label="广告素材">
      <div className="ad-detail-section-heading"><h3>广告图片</h3><span>福利专区按原图比例显示</span></div>
      {payload.placements.length ? <div className="ad-detail-creatives">{[welfareCreative(payload)].map(creative => <CreativePreview key={`${creative.placement}:${creative.image_url}`} creative={creative} />)}</div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无广告素材" />}
    </section>
  </div>;
}

function RevisionHistory({ detail }: { detail: AdDetail }) {
  if (!detail.revisions.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无修改与审核记录" />;
  return <div className="ad-detail-history"><Timeline items={detail.revisions.map(revision => ({
    color: revision.id === detail.ad.approved_revision_id ? "green" : "gray",
    children: <article className="ad-detail-revision">
      <div className="ad-detail-revision-heading"><strong>内容修改</strong>{revision.id === detail.ad.approved_revision_id && <Tag color="success" bordered={false}>已审核通过</Tag>}<time>{dateTime(revision.created_at)}</time></div>
      <p>{revision.change_note || "未填写修改说明"}</p>
      <div className="ad-detail-reviews">{detail.reviews.filter(review => review.revision_id === revision.id).map(review => <Alert key={review.id} showIcon type={review.decision === "approved" ? "success" : "error"} message={<span>{review.decision === "approved" ? "审核通过" : "审核驳回"}<time>{dateTime(review.created_at)}</time></span>} description={review.note || "无补充意见"} />)}</div>
      <Collapse ghost items={[{ key: "content", label: "查看当时内容", children: <PayloadDetail payload={revision.payload} showTitle /> }]} />
    </article>,
  }))} /></div>;
}

export function AdDetailDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const resource = useResource<AdDetail>(`/advertising/ads/${id}`);
  const { token } = theme.useToken();
  const variables = {
    "--ad-detail-text": token.colorText,
    "--ad-detail-muted": token.colorTextSecondary,
    "--ad-detail-border": token.colorBorderSecondary,
    "--ad-detail-surface": token.colorBgContainer,
    "--ad-detail-fill": token.colorFillQuaternary,
    "--ad-detail-accent": token.colorPrimary,
    "--ad-detail-radius": `${token.borderRadius}px`,
  } as CSSProperties;
  return <Drawer className="ad-detail-drawer" rootStyle={variables} title={<div className="ad-detail-drawer-title">广告详情</div>} open width="min(860px, 100vw)" onClose={onClose}>
    <Resource resource={resource}>{detail => {
      return <>
        <header className="ad-detail-overview">
          <div className="ad-detail-eyebrow">广告概览</div>
          <h2>{detail.ad.payload.title}</h2>
          <dl className="ad-detail-summary">
            <div><dt>当前内容审核</dt><dd><Tag bordered={false} color={reviewColors[detail.ad.status]}>{reviewLabels[detail.ad.status]}</Tag></dd></div>
            <div><dt>渠道编号</dt><dd>{detail.ad.channel_code ? <Typography.Text className="ad-detail-channel-code" copyable={{ tooltips: ["复制渠道编号", "已复制"] }}>{detail.ad.channel_code}</Typography.Text> : "—"}</dd></div>
          </dl>
          {detail.ad.master_paused && <Alert className="ad-detail-pause" type="warning" showIcon message="平台已暂停投放" description="需平台恢复后，此广告才可继续投放。" />}
        </header>
        <Tabs className="ad-detail-tabs" items={[
          { key: "current", label: "当前内容", children: <PayloadDetail payload={detail.ad.payload} /> },
          { key: "live", label: "已审核内容", children: detail.ad.approved_payload ? <PayloadDetail payload={detail.ad.approved_payload} showTitle /> : <Empty className="ad-detail-empty" image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无审核通过的内容" /> },
          { key: "history", label: <span>审核记录 <span className="ad-detail-count">{detail.revisions.length}</span></span>, children: <RevisionHistory detail={detail} /> },
        ]} />
      </>;
    }}</Resource>
  </Drawer>;
}
